import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const CSS_Align = () => {
  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">🎯 CSS Align</h1>

      <p className="tutorial-intro">
        CSS provides several ways to align text, inline elements, and block
        elements. Proper alignment makes content more readable and layouts more
        balanced.
      </p>

      {/* Why Align */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          borderRadius: "12px",
          marginBottom: "2rem",
          textAlign: "center",
        }}
      >
        <h2 style={{ color: "#92400e", marginBottom: "0.5rem" }}>
          🌟 Why Use Alignment?
        </h2>
        <p style={{ fontSize: "1.1rem", lineHeight: "1.6" }}>
          Alignment ensures that elements look organized on a page. You can
          center text, align images, and even center entire containers both
          horizontally and vertically.
        </p>
      </div>

      {/* 1. Text Alignment */}
      <div className="tutorial-section">
        <h2 className="section-title">1️⃣ Text Alignment</h2>
        <p>
          Use <code>text-align</code> to align inline content inside a block.
        </p>

        <CodeCopyBlock
          code={`p.left   { text-align: left; }
p.center { text-align: center; }
p.right  { text-align: right; }`}
        />

        <div style={{ marginTop: "1rem" }}>
          <p
            style={{ textAlign: "left", background: "#fee2e2", padding: "6px" }}
          >
            Left Aligned
          </p>
          <p
            style={{
              textAlign: "center",
              background: "#d9f99d",
              padding: "6px",
            }}
          >
            Center Aligned
          </p>
          <p
            style={{
              textAlign: "right",
              background: "#bfdbfe",
              padding: "6px",
            }}
          >
            Right Aligned
          </p>
        </div>
      </div>

      {/* 2. Block Alignment with Margin Auto */}
      <div className="tutorial-section">
        <h2 className="section-title">2️⃣ Block Alignment (margin: auto)</h2>
        <p>
          To horizontally center a block element (like a div), set a fixed{" "}
          <code>width</code> and use <code>margin: auto</code>.
        </p>

        <CodeCopyBlock
          code={`.center-box {
  width: 200px;
  margin: auto;
  background: #93c5fd;
  padding: 20px;
  text-align: center;
}`}
        />

        <div
          style={{
            width: "200px",
            margin: "auto",
            background: "#93c5fd",
            padding: "20px",
            textAlign: "center",
            borderRadius: "8px",
            marginTop: "1rem",
          }}
        >
          I’m centered with <code>margin: auto</code>
        </div>
      </div>

      {/* 3. Flexbox Alignment */}
      <div className="tutorial-section">
        <h2 className="section-title">3️⃣ Flexbox Alignment</h2>
        <p>
          Flexbox makes it easy to align items horizontally and vertically.
          Using <code>justify-content</code> and <code>align-items</code> you
          can center content perfectly.
        </p>

        <CodeCopyBlock
          code={`.flex-center {
  display: flex;
  justify-content: center; /* horizontal */
  align-items: center;     /* vertical */
  height: 150px;
  background: #86efac;
}`}
        />

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "150px",
            background: "#86efac",
            marginTop: "1rem",
            borderRadius: "8px",
          }}
        >
          <span
            style={{
              background: "#065f46",
              color: "white",
              padding: "10px 20px",
              borderRadius: "6px",
            }}
          >
            Centered with Flexbox
          </span>
        </div>
      </div>

      {/* 4. Grid Alignment */}
      <div className="tutorial-section">
        <h2 className="section-title">4️⃣ Grid Alignment</h2>
        <p>
          CSS Grid also allows alignment with <code>place-items: center;</code>.
        </p>

        <CodeCopyBlock
          code={`.grid-center {
  display: grid;
  place-items: center; /* both horizontal & vertical */
  height: 150px;
  background: #fbbf24;
}`}
        />

        <div
          style={{
            display: "grid",
            placeItems: "center",
            height: "150px",
            background: "#fbbf24",
            marginTop: "1rem",
            borderRadius: "8px",
          }}
        >
          <span
            style={{
              background: "#92400e",
              color: "white",
              padding: "10px 20px",
              borderRadius: "6px",
            }}
          >
            Centered with Grid
          </span>
        </div>
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">🚀 Next Steps</h2>
        <p>
          Now that you know text, block, flex, and grid alignment, explore{" "}
          <a href="/css/position" className="learn-more">
            CSS Position
          </a>{" "}
          for even more control over element placement.
        </p>
      </div>
    </div>
  );
};

export default CSS_Align;
